/**
 * UserQuestion — interrupt-driven multi-step question card.
 *
 * When the agent calls `ask_user_questions`, the graph interrupts with
 * { type: 'user_question', questions: [...] }. This component replaces the
 * composer, walks the user through each question one at a time, and resumes
 * the graph with the collected answers when the user clicks send.
 *
 * Mirrors the TaskReview / IntegrationSetup pattern.
 */

import { useCallback, useMemo, useState, type FC } from 'react';
import {
  useLangGraphInterruptState,
  useLangGraphSendCommand,
} from '@assistant-ui/react-langgraph';
import { ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon, PencilIcon } from 'lucide-react';
import { cn } from '@/client/lib/utils';

// ---------------------------------------------------------------------------
// Types (mirrors backend Question / QuestionOption)
// ---------------------------------------------------------------------------

interface QuestionOption {
  label: string;
  value: string;
  description: string;
  tag?: string;
}

interface Question {
  id: string;
  text: string;
  mode: 'single' | 'multi';
  options: QuestionOption[];
}

interface UserQuestionValue {
  type: 'user_question';
  questions: Question[];
}

function isQuestion(v: unknown): v is Question {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.text === 'string' &&
    (o.mode === 'single' || o.mode === 'multi') &&
    Array.isArray(o.options)
  );
}

function isUserQuestionValue(v: unknown): v is UserQuestionValue {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    o.type === 'user_question' && Array.isArray(o.questions) && o.questions.every(isQuestion)
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useUserQuestionValue(): UserQuestionValue | null {
  const interruptState = useLangGraphInterruptState();
  const value = interruptState?.value;
  return isUserQuestionValue(value) ? value : null;
}

export function useIsUserQuestionMode(): boolean {
  return useUserQuestionValue() !== null;
}

// ---------------------------------------------------------------------------
// Per-question answer state
// ---------------------------------------------------------------------------

interface AnswerState {
  selected: string[]; // option values picked from the list (single mode keeps at most one)
  customText: string; // "Something else" free-text answer
  customExpanded: boolean;
}

const EMPTY_ANSWER: AnswerState = {
  selected: [],
  customText: '',
  customExpanded: false,
};

function computeAnswer(question: Question, state: AnswerState): string | string[] | undefined {
  const trimmed = state.customText.trim();
  if (question.mode === 'single') {
    if (trimmed) return trimmed;
    return state.selected[0];
  }
  const values = [...state.selected];
  if (trimmed) values.push(trimmed);
  return values.length > 0 ? values : undefined;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

interface OptionRowProps {
  option: QuestionOption;
  selected: boolean;
  mode: 'single' | 'multi';
  onClick: () => void;
}

const OptionRow: FC<OptionRowProps> = ({ option, selected, mode, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-start gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left transition-colors',
        selected ? 'bg-[hsl(255_100%_96%)]' : 'hover:bg-muted',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-[18px] shrink-0 items-center justify-center border-[1.5px] transition-colors',
          mode === 'single' ? 'rounded-full' : 'rounded-[5px]',
          selected ? 'border-[hsl(262_60%_55%)] bg-[hsl(262_60%_55%)]' : 'border-border',
        )}
      >
        {selected && mode === 'single' && (
          <span className="size-1.5 rounded-full bg-white" />
        )}
        {selected && mode === 'multi' && (
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path
              d="M2.5 6L5 8.5L9.5 3.5"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium leading-[1.4] text-foreground">
            {option.label}
          </span>
          {option.tag && (
            <span className="inline-flex items-center rounded-md bg-[hsl(255_100%_96%)] px-1.5 py-px text-[10.5px] font-medium leading-[1.4] text-[hsl(262_60%_55%)]">
              {option.tag}
            </span>
          )}
        </span>
        {option.description && (
          <span className="mt-px block text-[11.5px] leading-[1.4] text-muted-foreground">
            {option.description}
          </span>
        )}
      </span>
    </button>
  );
};

interface FreetextRowProps {
  expanded: boolean;
  value: string;
  onExpand: () => void;
  onChange: (v: string) => void;
}

const FreetextRow: FC<FreetextRowProps> = ({ expanded, value, onExpand, onChange }) => {
  if (expanded) {
    return (
      <div className="flex min-h-[40px] items-center gap-2.5 px-2 py-2">
        <PencilIcon className="size-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer..."
          className="flex-1 bg-transparent text-[13.5px] text-foreground outline-none placeholder:text-muted-foreground"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onExpand}
      className="flex min-h-[40px] w-full cursor-pointer items-center gap-2.5 px-2 py-2 text-left text-[13.5px] text-muted-foreground transition-colors hover:text-foreground"
    >
      <PencilIcon className="size-4 shrink-0" />
      Something else
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const UserQuestion: FC = () => {
  const value = useUserQuestionValue();
  const sendCommand = useLangGraphSendCommand();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = useMemo(() => value?.questions ?? [], [value]);
  const total = questions.length;
  const safeStep = Math.min(stepIndex, Math.max(total - 1, 0));
  const current = questions[safeStep];
  const isFirst = safeStep === 0;
  const isLast = safeStep === total - 1;
  const hasFeedback = feedback.trim().length > 0;
  const canSubmit = !isSubmitting && (isLast || hasFeedback);

  const getAnswer = useCallback(
    (questionId: string): AnswerState => answers[questionId] ?? EMPTY_ANSWER,
    [answers],
  );

  const updateAnswer = useCallback(
    (questionId: string, patch: Partial<AnswerState> | ((prev: AnswerState) => AnswerState)) => {
      setAnswers((prev) => {
        const existing = prev[questionId] ?? EMPTY_ANSWER;
        const next = typeof patch === 'function' ? patch(existing) : { ...existing, ...patch };
        return { ...prev, [questionId]: next };
      });
    },
    [],
  );

  const handleOptionClick = useCallback(
    (questionId: string, mode: 'single' | 'multi', optionValue: string) => {
      updateAnswer(questionId, (prev) => {
        if (mode === 'single') {
          // Selecting a regular option clears any custom text
          return { ...prev, selected: [optionValue], customText: '', customExpanded: false };
        }
        const has = prev.selected.includes(optionValue);
        return {
          ...prev,
          selected: has
            ? prev.selected.filter((v) => v !== optionValue)
            : [...prev.selected, optionValue],
        };
      });
    },
    [updateAnswer],
  );

  const handleExpandCustom = useCallback(
    (questionId: string, mode: 'single' | 'multi') => {
      updateAnswer(questionId, (prev) => ({
        ...prev,
        customExpanded: true,
        // For single mode, picking 'Something else' clears the radio selection
        selected: mode === 'single' ? [] : prev.selected,
      }));
    },
    [updateAnswer],
  );

  const handleCustomChange = useCallback(
    (questionId: string, text: string) => {
      updateAnswer(questionId, { customText: text });
    },
    [updateAnswer],
  );

  const finalQuestions = useMemo(
    () =>
      questions.map((q) => {
        const answer = computeAnswer(q, getAnswer(q.id));
        return answer === undefined ? q : { ...q, answer };
      }),
    [questions, getAnswer],
  );

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await sendCommand({
        resume: {
          questions: finalQuestions,
          message: feedback.trim() || null,
        } as unknown as string,
      });
    } finally {
      setIsSubmitting(false);
      setFeedback('');
    }
  }, [finalQuestions, feedback, sendCommand]);

  if (!value || total === 0 || !current) return null;

  const currentAnswer = getAnswer(current.id);

  return (
    <div className="overflow-hidden rounded-2xl border border-input bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-[13px] font-semibold text-foreground">
          {total > 1 ? 'Questions' : 'Question'}
        </span>
        {total > 1 && (
          <span className="text-[11px] font-medium text-muted-foreground">
            {safeStep + 1} of {total}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 pb-2 pt-3">
        <div className="mb-2.5 text-[13px] font-medium text-foreground">{current.text}</div>
        <div className="-mx-2">
          {current.options.map((option) => {
            const selected = currentAnswer.selected.includes(option.value);
            return (
              <OptionRow
                key={option.value}
                option={option}
                mode={current.mode}
                selected={selected}
                onClick={() => handleOptionClick(current.id, current.mode, option.value)}
              />
            );
          })}
          <FreetextRow
            expanded={currentAnswer.customExpanded}
            value={currentAnswer.customText}
            onExpand={() => handleExpandCustom(current.id, current.mode)}
            onChange={(t) => handleCustomChange(current.id, t)}
          />
        </div>
      </div>

      {/* Nav (only when more than one question) */}
      {total > 1 && (
        <div
          className={cn(
            'flex items-center border-t px-4 py-2.5',
            isLast ? 'justify-start' : 'justify-between',
          )}
        >
          <button
            type="button"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={isFirst}
            className={cn(
              'flex items-center gap-1 rounded-lg border border-border bg-transparent px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors',
              isFirst
                ? 'cursor-not-allowed opacity-35'
                : 'cursor-pointer hover:bg-muted',
            )}
          >
            <ChevronLeftIcon className="size-3" />
            Back
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={() => setStepIndex((i) => Math.min(total - 1, i + 1))}
              className="flex cursor-pointer items-center gap-1 rounded-lg border border-transparent bg-foreground px-3.5 py-1.5 text-[12px] font-medium text-background transition-opacity hover:opacity-85"
            >
              Next
              <ChevronRightIcon className="size-3" />
            </button>
          )}
        </div>
      )}

      {/* Feedback + send. Send is enabled either on the last step
          (submitting collected answers) or whenever the user has typed
          feedback (submitting a conversational reply from any step). */}
      <div className="flex items-center gap-2 border-t px-4 py-2.5">
        <input
          type="text"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing &&
              canSubmit
            ) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          placeholder="Add feedback or reply..."
          className="flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          aria-label="Submit"
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full bg-[hsl(262_60%_55%)] text-white transition-opacity',
            canSubmit
              ? 'cursor-pointer hover:opacity-85'
              : 'cursor-not-allowed opacity-35',
          )}
        >
          <ArrowUpIcon className="size-4" />
        </button>
      </div>
    </div>
  );
};
